import { Show } from '@/components/show'
import type { PageLayoutProps } from '@healthnz-prm/fhir-engine-renderer/types'
import { ModalManager } from '@/components/modalManager/ModalManager'
import { ContentLoader } from '@/components/shared'
import { NextSubmitButtons } from '@/components/fhirEngineClinical/nextSubmitButtons'
import { useSubmitState } from '@/contexts/submitState/submitState'
import { useEmbed } from '@/contexts/embed/embed'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FieldGroup } from '@/components/ui/field'
import { ArrowLeft02Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import { Children } from 'react'
import { Separator } from '@/components/ui/separator'

export default function PageLayout({
  children,
  isPartialSaveLoading,
  questionnaireTitle,
  questionnaireTitleComponent,
  onPrev,
  onNext,
  onSubmit,
  submitButtonLabel,
  title,
  validate
}: PageLayoutProps) {
  const { submitState } = useSubmitState()
  const embed = useEmbed()

  const handleNext =
    onNext &&
    (() => {
      onNext()
      embed.onPageChange()
    })

  const handlePrev =
    onPrev &&
    (() => {
      onPrev()
      embed.onPageChange()
    })

  return (
    <div className='space-y-4'>
      <Show
        when={
          !embed.isEmbedded &&
          (questionnaireTitleComponent || questionnaireTitle)
        }
      >
        <h1 className='text-lg font-bold'>
          {questionnaireTitleComponent ?? questionnaireTitle}
        </h1>
      </Show>
      <ModalManager />
      <Show when={['ready', 'invalid'].includes(submitState)}>
        <Show
          when={onPrev}
          fallback={
            <Show when={embed.showQuitButton}>
              <Button onClick={() => embed.onExit('back')} variant='link'>
                <HugeiconsIcon icon={ArrowLeft02Icon} />
                Back
              </Button>
            </Show>
          }
        >
          <Button onClick={handlePrev} variant='link'>
            <HugeiconsIcon icon={ArrowLeft02Icon} />
            Previous step
          </Button>
        </Show>
      </Show>

      <Card>
        <Show when={title}>
          <CardHeader>
            <h1 className='text-lg font-semibold'>{title}</h1>
            <Separator />
          </CardHeader>
        </Show>

        <CardContent>
          <ContentLoader state={submitState} loadingMessage='Submitting'>
            <FieldGroup>
              {Children.map(children, (child, index) => (
                <>
                  {index > 0 && <Separator />}
                  {child}
                </>
              ))}
            </FieldGroup>
          </ContentLoader>
        </CardContent>

        <Separator />

        <CardFooter className='flex-col gap-2'>
          <NextSubmitButtons
            hasSubmit={!!onSubmit}
            isPartialSaveLoading={isPartialSaveLoading}
            onNext={handleNext}
            submitButtonLabel={submitButtonLabel}
            submitState={submitState}
            validate={validate}
          />
          <Show when={embed.showQuitButton && submitState !== 'success'}>
            <Button onClick={() => embed.onExit('quit')} variant='link'>
              Quit
            </Button>
          </Show>
        </CardFooter>
      </Card>
    </div>
  )
}
